import { Box, TextField, Typography } from '@mui/material';
import { FlexBox } from 'components/Containers/FlexBox';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import { MultiSelector } from 'components/Form/Selectors/MultiSelector';
import { Selector } from 'components/Form/Selectors/Selector';
import UserHandler from 'handlers/internal.user.handler';
import { useEffect, useState } from 'react';
import { modalInputFieldStyle } from 'styles/global.css';
import { type InternalUser } from 'types/user.types';
import { USER_DEPARTMENT_OPTIONS } from 'utils/enums';
import { createOrUpdateInternalUserRequestBodySchema } from 'utils/schemas/user.schema';
import { validateYupSchema } from 'utils/validators/schema.validator';
import {
  handleSelectCustomerAll,
  handleSelectCustomerSingle,
} from '../../helpers/common';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { type IUpdateInternalUserPayload } from 'types/payloads/user.payload.types';
import { useAppSelector } from 'store/hook';
import { type Customer } from 'types/customer.types';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { ThemePalette } from 'mui.theme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  title: string;
  open: boolean;
  userData: InternalUser;
  handleAbort: () => void;
  enableDarkTheme: boolean;
}

export const UpdateInternalUserModal = ({
  title,
  handleAbort,
  open,
  userData,
  enableDarkTheme,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const userHandler = new UserHandler();

  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const customers = useAppSelector((state) => state.customerReducer.customers);
  const [isValidForm, setFormValidity] = useState(false);
  // We don't update the isActive field or the userId field
  const [internalUser, setInternalUser] = useState<IUpdateInternalUserPayload>({
    firstName: userData.firstName,
    lastName: userData.lastName,
    // example: johndoe@symboticware.com -> johndoe
    email: userData.email,
    department: userData.department,
    userCustomerAssociations: userData.userCustomerAssociations,
    phoneNumber: userData.phoneNumber,
    preferredTimeDisplayMode: userData.preferredTimeDisplayMode,
  });

  const customerState: InternalUser['userCustomerAssociations'] = customers.map(
    (customer: Customer) => {
      return {
        id: customer.customerId,
        display: customer.customerName,
      };
    }
  );

  // Validate form as user fills out fields
  useEffect(() => {
    const validateForm = async () =>
      await validateYupSchema(
        internalUser,
        createOrUpdateInternalUserRequestBodySchema
      );

    void validateForm().then((isValid) => {
      setFormValidity(isValid);
    });
  }, [internalUser]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setInternalUser({
      ...internalUser,
      [name]: value,
    });
  };

  const handleSuccess = async () => {
    await userHandler.updateInternalUser(userData.userId, internalUser);
    // this open/close setter will not be abstracted out because it is specific to this view
    handleAbort();
  };

  const handlePhoneChange = (value: string) => {
    setInternalUser({
      ...internalUser,
      phoneNumber: value,
    });
  };

  /**
   * This is the form that will be rendered in the modal
   *
   * First name
   * Last name
   * Email
   * Department (dropdown)
   * Associated Customer Accounts (dropdown)
   * userId (non-editable)
   * login acount (non-editable)
   * last login (non-editable)
   */
  return (
    <InteractiveModal
      initialState={open}
      SuccessButtonProps={{
        disabled: !isValidForm,
        onClick: handleSuccess,
        label: t('save_edits_and_return'),
      }}
      handleAbort={handleAbort}
      title={title}
      confirmCancellation
      enableDarkTheme={enableDarkTheme}
    >
      {/* First Name TextField */}
      <SWTextField
        key="first-name"
        name="firstName"
        label={t('first_name')}
        TextFieldProps={{
          name: 'firstName',
          value: internalUser.firstName,
          onChange: handleChange,
          InputProps: {
            sx: { ...modalInputFieldStyle, height: '37px' },
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Last Name TextField */}
      <SWTextField
        key="last-name"
        name="lastName"
        label={t('last_name')}
        TextFieldProps={{
          name: 'lastName',
          value: internalUser.lastName,
          onChange: handleChange,
          InputProps: {
            sx: { ...modalInputFieldStyle, height: '37px' },
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Email  TextField */}
      <Box key="email">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('email_address')}
        </Typography>
        <FlexBox>
          <TextField
            name="email"
            onChange={handleChange}
            value={internalUser.email} // will have to omit domain
            InputProps={{
              sx: {
                height: '37px',
                minWidth: '131px',
                color: palette.textPrimary,
              },
            }}
            sx={{
              color: palette.textPrimary,
            }}
          />
          {/* <Typography
            variant="body2"
            fontSize="12px"
            display="flex"
            alignItems="center"
            sx={{
              color: palette.textPrimary,
            }}
          >
            {INTERNAL_USER_DOMAIN}
          </Typography> */}
        </FlexBox>
      </Box>

      {/* Department selector drop down single */}
      <Box key="department">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('department')}
        </Typography>
        <Selector
          value={internalUser.department}
          onChange={(event, child) => {
            setInternalUser({
              ...internalUser,
              department: event.target.value.toString(),
            });
          }}
          selectorOptions={USER_DEPARTMENT_OPTIONS}
          enableDarkTheme={enableDarkTheme}
        />
      </Box>

      {/* Customer account multi selector */}
      <Box key="customer-accounts">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('associated_customer_accounts')}
        </Typography>
        <MultiSelector
          value={internalUser.userCustomerAssociations}
          renderValue={(selected) => {
            return `${selected?.length || 0} Customer(s) Selected`;
          }}
          handleSelectOne={(event) => {
            handleSelectCustomerSingle(event, internalUser, setInternalUser);
          }}
          handleSelectAll={() => {
            handleSelectCustomerAll(
              customerState,
              internalUser,
              setInternalUser
            );
          }}
          enableDarkTheme={enableDarkTheme}
          /*
        This stubbed response be replaced with real customer accounts
        */
          selectorOptions={customerState}
        />
      </Box>

      {/** Phone number box */}
      <Box key="phone-number">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('phone_number_2')}
        </Typography>
        <PhoneInput
          country={'ca'}
          value={internalUser.phoneNumber}
          onChange={handlePhoneChange}
          inputStyle={{ width: '270px', height: '20px', minWidth: '270px' }}
          enableSearch={true}
          enableAreaCodes={true}
          buttonStyle={{
            border: 'none',
            backgroundColor: 'transparent',
          }}
          placeholder=""
        />
      </Box>

      <FlexBox></FlexBox>

      {/* Non updateable customer data starts here */}
      <Box key="user-id" mt="40px">
        <Typography
          variant="bodyBold"
          display="block"
          mb="4px"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('user_id')}
        </Typography>
        <Typography
          variant="small"
          display="block"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {userData.userId}
        </Typography>
      </Box>
      <Box key="login-count" mt="40px">
        <Typography
          variant="bodyBold"
          display="block"
          mb="4px"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('number_of_logins')}
        </Typography>
        <Typography
          variant="small"
          display="block"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {userData.loginCount}
        </Typography>
      </Box>
      <Box key="last-login" mt="40px">
        <Typography
          variant="bodyBold"
          display="block"
          mb="4px"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('last_login')}
        </Typography>
        <Typography
          variant="small"
          display="block"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {userData.lastLogin
            ? convertUtcToDisplayTime(
                userData.lastLogin,
                preferredTimeDisplayMode,
                timezone
              )
            : null}
        </Typography>
      </Box>
    </InteractiveModal>
  );
};
